/**
 * Best-effort dirty heuristic (BR-08): focused editable or open form-bearing dialog.
 * Used only to defer SW hard reload (BR-07) — not to block chunk-fail hard path.
 */
export function isAppUpdateDirty(root: ParentNode = document): boolean {
  if (isFocusedEditable(root)) {
    return true;
  }

  return hasOpenFormBearingDialog(root);
}

function isFocusedEditable(root: ParentNode): boolean {
  const active = root instanceof Document ? root.activeElement : null;
  if (!active || !(active instanceof HTMLElement)) {
    return false;
  }

  if (active.isContentEditable) {
    return true;
  }

  const tag = active.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") {
    return true;
  }

  if (tag === "INPUT") {
    const type = ((active as HTMLInputElement).type || "text").toLowerCase();
    // Non-editable input types do not count as dirty by themselves.
    if (
      type === "button" ||
      type === "submit" ||
      type === "reset" ||
      type === "checkbox" ||
      type === "radio" ||
      type === "range" ||
      type === "file" ||
      type === "hidden" ||
      type === "image" ||
      type === "color"
    ) {
      return false;
    }
    return true;
  }

  return false;
}

function hasOpenFormBearingDialog(root: ParentNode): boolean {
  const dialogs = root.querySelectorAll(
    '[role="dialog"], [data-slot="dialog-content"], [data-slot="dialog"]',
  );

  for (const dialog of dialogs) {
    if (!(dialog instanceof HTMLElement)) {
      continue;
    }

    if (!isDialogOpen(dialog)) {
      continue;
    }

    // Skip our own hard dialog — it is not a user form (E-14 nuance).
    if (dialog.closest("[data-app-update-hard-dialog]") || dialog.hasAttribute("data-app-update-hard-dialog")) {
      continue;
    }

    if (dialog.querySelector("form")) {
      return true;
    }
  }

  return false;
}

function isDialogOpen(dialog: HTMLElement): boolean {
  const state = dialog.getAttribute("data-state") ?? dialog.getAttribute("data-open");
  if (state === "closed" || state === "false") {
    return false;
  }

  // Base UI / shadcn often omit closed nodes; presence + visibility is enough.
  const style = typeof window !== "undefined" ? window.getComputedStyle(dialog) : null;
  if (style && (style.display === "none" || style.visibility === "hidden")) {
    return false;
  }

  return true;
}
